<template>
  <v-expansion-panel-header
    dark
    class="device-informations-wrapper"
    :class="{ selected: props.deviceSelected, disconnected: disconnected }"
  >
    <v-checkbox
      v-if="getCurrentUserIsAbleToWrite('commandsManagement')"
      :id="`device-list-select-${props.device.id}`"
      class="device-selector"
      :color="props.deviceSelected ? themeColors.secondary : themeColors.white"
      :input-value="props.deviceSelected"
      on-icon="mdi-checkbox-intermediate"
      @click="e => onDeviceSelect(e)"
    />
    <div class="device-activity-wrapper">
      <div class="device-attribute pl-0 pr-2">
        <img v-if="props.device.type" :src="require(`@/assets/images/${props.device.type}.svg`)" :alt="`icon-${props.device.type}`">
        <span
          v-if="props.device.type"
          :id="`device-type-${props.device.id}`"
          :class="isActive"
          class="pl-2"
        >
          {{ getDeviceTypeLabel(props.device.type) }}
        </span>
        <span v-else>-</span>
      </div>
      <div :id="`device-list-id-${props.device.id}`" class="device-attribute ml-0" :class="isActive">
        {{ props.device.shortDeviceId }}
        <v-icon
          :id="`device-${props.device.id}-copy`"
          class="copy-device-id"
          :title="$t('device.list.item.copy_to_clipboard')"
          @click="e => copyToClipboard(e, props.device.shortDeviceId)"
        >
          mdi-content-copy
        </v-icon>
      </div>
      <div :id="`device-list-customer-${props.device.id}`" class="device-attribute ml-0" :class="isActive">
        {{ props.device.customer ? props.device.customer.tspId : '&nbsp;' }}
      </div>
      <div class="device-attribute">
        <span v-if="getDevicePerso && tollingState && tollingState.state !== 'unpersonalized' && !getDevicePerso.isDeperso">
          <img
            v-if="tollingState.reason && props.device.deviceTollingState.inProgress"
            :id="`device-${props.device.id}-state-${tollingState.state}-progress`"
            class="status-inprogress-icon"
            :src="require(`@/assets/icons/status/${tollingState.state}_grey.svg`)"
            :title="$t(`device.update.states.pending_${tollingState.state}`)"
            alt="status-lock"
          >
          <img
            v-else
            :id="`device-${props.device.id}-state-${tollingState.state}`"
            :class="props.device.deviceTollingState.inProgress ? 'status-inprogress-icon' : 'status-icon'"
            :src="props.device.deviceTollingState.inProgress ? require(`@/assets/icons/status/pending_${tollingState.state}.svg`) : require(`@/assets/icons/status/${tollingState.state}.svg`)"
            :title="props.device.deviceTollingState.inProgress ? $t(`device.update.states.pending_${tollingState.state}`) : $t(`device.update.states.${tollingState.state}`)"
            alt="status-notlock"
          >
          <img
            v-if="tollingState.reason"
            :id="`device-${props.device.id}-reason-${tollingState.reason}`"
            class="reason-icon"
            :src="require(`@/assets/icons/status/${tollingState.reason}.svg`)"
            :title="$t(`device.update.states.${tollingState.reason}`)"
            alt="reason"
          >
          <img
            v-if="tollingState.reason && props.device.deviceTollingState.inProgress"
            :id="`device-${props.device.id}-state-${tollingState.state}-pending`"
            class="status-inprogress-icon"
            :src="require(`@/assets/icons/status/pending_active.svg`)"
            :title="$t(`device.update.states.${tollingState.state}`)"
            alt="reason-with-state"
          >
        </span>
        <span v-else />
      </div>
      <div class="device-attribute">
        <div class="associated-plate-number">
          <img
            v-if="vehicleIconCss && vehicleIconCss !== 'unknown'"
            :id="`device-${props.device.id}-vehicle-state-${vehicleIconCss}`"
            :src="require(`@/assets/icons/perso/status/${vehicleIconCss}.svg`)"
            :class="vehicleIconCss"
            :title="$t(statusTooltip)"
            :alt="$t(statusTooltip)"
          >
          <span
            v-if="devicePlateNumber && vehicleIconCss !== '' && vehicleIconCss !== 'unknown'"
            :id="`device-${props.device.id}-vehicle-plate-${devicePlateNumber}`"
            class="plate-number"
            :class="vehicleIconCss === 'incompleted' || vehicleIconCss === 'failure' ? `chip chip-${vehicleIconCss}` : ''"
            @click.stop="redirectTo(props.device, 'personalisation')"
          >{{ devicePlateNumber }}</span>
          <span v-else class="noplatenumber" :title="$t(statusTooltip)" />
        </div>
      </div>
      <div class="device-attribute device-activity-details">
        <div
          :id="`device-${props.device.id}-connectivity`"
          :class="isActive"
        >
          {{ updatedAtHumanized }}
        </div>
      </div>
      <div>
        <v-icon :title="$t('tolling.tracking_records.title')" @click.stop="overlay = true">
          mdi-map-marker-outline
        </v-icon>
        <v-icon class="mx-5" :title="$t('device.history.title')" @click.stop="redirectTo(props.device, 'history')">
          mdi-history
        </v-icon>
      </div>
    </div>
    <TrackingRecords :device="props.device" :overlay="overlay" @close="overlay = false" />
  </v-expansion-panel-header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import useStore from '@/utils/store/common';
import { useI18n } from 'vue-i18n-composable';
import { useRouter } from 'vue-router/composables';
import moment from 'moment';
import { colors } from '@/constants/theme/colors';
import { DEVICE_ACTIVE_DURATION_THRESHOLD_IN_SECONDS, DEVICE_DISCONNECTED_DURATION_THRESHOLD_IN_SECONDS } from '@/constants/views/DevicesList';
import TrackingRecords from '@/components/common/TrackingRecords/TrackingRecords.vue';
import { V2Device } from 'ctp-search-device';
import { IDevicesListState } from '@/store/modules/devices/types';

const props = defineProps({
  device: {
    type: Object,
    default: () => ({} as V2Device)
  },
  devicesListState: {
    type: Object,
    default: () => ({} as IDevicesListState)
  },
  deviceSelected: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['onDeviceSelect']);

const store = useStore();
const router = useRouter();
const i18n = useI18n();

const now = moment();
const themeColors = colors;
const overlay = ref(false);

function getDevicePerso() {
  if (props.device.devicePersonalisationState && props.device.devicePersonalisationState.inProgress) {
    return props.device.devicePersonalisationState.targetPersonalisation;
  }
  if (props.device.devicePersonalisationState && props.device.devicePersonalisationState.currentPersonalisation) {
    return props.device.devicePersonalisationState!.currentPersonalisation;
  }
  return null;
}

function getCurrentUserIsAbleToWrite(featureName: string) {
  return store.getters['permissions/getCurrentUserIsAbleTo']('write', featureName);
}

function onDeviceSelect(e: Event): void {
  e.stopPropagation();
  emit('onDeviceSelect', props.device.id);
}

function copyToClipboard(e: Event, content: any) {
  e.stopPropagation();
  const dummyNode = document.createElement('textarea');
  document.body.appendChild(dummyNode);
  dummyNode.value = content;
  dummyNode.select();
  document.execCommand('copy');
  document.body.removeChild(dummyNode);
}

async function redirectTo(device: V2Device, path: string) {
  let id = device.shortDeviceId;
  if (device.type === 'vdolink' && path === 'personalisation') {
    id = device.devicePersonalisationState?.currentPersonalisation?.persoTargetID;
  }
  router.push({
    name: path,
    params: {
      deviceId: id || ''
    }
  });
}

function getDeviceTypeLabel(deviceType: string) {
  let label = '';
  switch (deviceType) {
  case 'obu5gen':
    label = 'OBU5+';
    break;
  case 'vdolink':
    label = 'VDO Link';
    break;
  default:
    label = 'unknown';
    break;
  }
  return label;
}

function humanizeDuration(date?: Date): string {
  return moment()
    .subtract(moment.duration(now.diff(date)))
    .locale(i18n.locale.value)
    .fromNow();
}

watch(
  () => i18n.locale.value,
  () => humanizeDuration()
);
const updatedAtHumanized = computed(() => {
  if (props.devicesListState.error.devicesActivity) {
    return `${i18n.t('common.status.na')} (${i18n.t('common.status.unavailable_data')})`;
  }
  return props.device.lastSeenTS ? humanizeDuration(moment.unix(props.device.lastSeenTS).toDate()) : `${i18n.t('device.list.never_connected')}`;
});

const disconnected = computed(() => {
  const lastSeen = props.device.lastSeenTS ? moment.unix(props.device.lastSeenTS).toDate() : false;
  return !lastSeen || (Math.abs(moment.duration(now.diff(lastSeen)).asSeconds()) >= DEVICE_DISCONNECTED_DURATION_THRESHOLD_IN_SECONDS);
});

const isActive = computed(() => {
  const lastSeen = props.device.lastSeenTS ? moment.unix(props.device.lastSeenTS).toDate() : false;
  return lastSeen && (Math.abs(moment.duration(now.diff(lastSeen)).asSeconds()) <= DEVICE_ACTIVE_DURATION_THRESHOLD_IN_SECONDS) ? 'device-online' : 'device-offline';
});

const tollingState = computed(() => {
  if (props.device.deviceTollingState && props.device.deviceTollingState.inProgress) {
    return props.device.deviceTollingState.targetState;
  }
  if (props.device.deviceTollingState && props.device.deviceTollingState.currentState) {
    return props.device.deviceTollingState.currentState;
  }
  return null;
});

const devicePlateNumber = computed(() => {
  const perso = getDevicePerso();
  if (perso) {
    return perso.vehicleInfo && perso.vehicleInfo.vehicleLicencePlateNumber?.licencePlateNumber;
  }
  return undefined;
});

const vehicleIconCss = computed(() => {
  const perso = getDevicePerso();
  if (perso && perso.isDeperso) {
    return props.device.devicePersonalisationState!.inProgress ? 'deperso_pending' : 'deperso_current';
  }
  if (perso && props.device.devicePersonalisationState!.inProgress) {
    return 'pending';
  }
  if (perso && !perso.isDeperso && perso.vehicleInfo) {
    return 'current';
  }
  return 'unknown';
});

const statusTooltip = computed(() => {
  const perso = getDevicePerso();
  if (perso && !perso.isDeperso) {
    return props.device.devicePersonalisationState!.inProgress ? 'device.list.item.personalization_in_progress' : 'device.list.item.device_associated';
  } if (perso) {
    return props.device.devicePersonalisationState!.inProgress ? 'device.list.item.depersonalization_in_progress' : 'device.list.item.depersonalized';
  } return '';
});

</script>

<style lang="scss" scoped>
@import './styles/device-informations.scss';
</style>
